import type { QaidaLesson } from "../types";

export const QAIDA_LESSONS: QaidaLesson[] = [
  {
    id: "letters", number: 1, title: "Single Arabic letters", arabicTitle: "الحروف المفردة",
    summary: "Recognise every Arabic letter in its isolated form and pronounce it from its proper articulation point.",
    teacherNote: "Keep letter names distinct from their sounds. Do not add an /a/ vowel after a bare letter.",
    objectives: ["Name all 29 letters", "Distinguish similar shapes", "Begin correct makharij"],
    examples: [
      { arabic: "ا ب ت ث", transliteration: "Alif, Bā, Tā, Thā", note: "Thā: tongue tip lightly between the teeth." },
      { arabic: "ج ح خ", transliteration: "Jīm, Ḥā, Khā", note: "Three shapes; three different articulation points." },
      { arabic: "د ذ ر ز", transliteration: "Dāl, Dhāl, Rā, Zā" },
      { arabic: "س ش ص ض", transliteration: "Sīn, Shīn, Ṣād, Ḍād", note: "Ṣād and Ḍād are heavy." },
      { arabic: "ط ظ ع غ", transliteration: "Ṭā, Ẓā, ʿAyn, Ghayn" },
      { arabic: "ف ق ك ل م ن ه و ي", transliteration: "Fā through Yā" }
    ],
    practice: ["Point to letters in random order", "Group throat, tongue, and lip letters", "Repeat each letter five times without a vowel"],
    check: { question: "Which letter is pronounced with the tongue tip between the teeth?", answers: ["ب", "ث", "ك"], correct: 1 }
  },
  {
    id: "joined", number: 2, title: "Joined and compound letters", arabicTitle: "الحروف المركبة",
    summary: "Recognise how letters change at the beginning, middle, and end of a word.",
    teacherNote: "Train the eye to find the dots and the underlying letter skeleton before reading.",
    objectives: ["Identify positional forms", "Know the six non-connecting letters", "Read two- and three-letter shapes"],
    examples: [
      { arabic: "بـ ـبـ ـب", transliteration: "b — initial, medial, final" },
      { arabic: "تَبَ بَتَ كَتَبَ", transliteration: "taba, bata, kataba" },
      { arabic: "ا د ذ ر ز و", transliteration: "Letters that do not join to the following letter" },
      { arabic: "مُحَمَّدٌ", transliteration: "Muḥammadun" }
    ],
    practice: ["Circle every non-connecting letter", "Build words with letter cards", "Read without spelling the letter names"],
    check: { question: "Which letter does not connect to the letter after it?", answers: ["ب", "ر", "س"], correct: 1 }
  },
  {
    id: "muqattaat", number: 3, title: "Disjointed letters", arabicTitle: "الحروف المقطعات",
    summary: "Read the special opening letters of selected surahs by their letter names, with their required elongation.",
    teacherNote: "These are recited as letter names, not as ordinary joined words. Preserve each madd count.",
    objectives: ["Recognise the 14 opening letters", "Read letter names", "Apply 2- or 6-count madd as taught"],
    examples: [
      { arabic: "الٓمٓ", transliteration: "Alif Lām Mīm" },
      { arabic: "كٓهيعٓصٓ", transliteration: "Kāf Hā Yā ʿAyn Ṣād" },
      { arabic: "طٰهٰ", transliteration: "Ṭā Hā" },
      { arabic: "يسٓ", transliteration: "Yā Sīn" },
      { arabic: "حمٓ", transliteration: "Ḥā Mīm" }
    ],
    practice: ["Listen and echo slowly", "Tap once per madd count", "Locate openings in a mushaf"],
    check: { question: "How is الٓمٓ read?", answers: ["alam", "Alif Lām Mīm", "alīm"], correct: 1 }
  },
  {
    id: "harakat", number: 4, title: "Short vowels (Harakat)", arabicTitle: "الحركات",
    summary: "Read fatḥah, kasrah, and ḍammah as one-beat short vowels.",
    teacherNote: "A short vowel must stay short; do not turn بَ into بَا or بِ into بِي.",
    objectives: ["Identify all three harakat", "Read one beat only", "Blend letters smoothly"],
    examples: [
      { arabic: "بَ بِ بُ", transliteration: "ba, bi, bu" },
      { arabic: "تَ تِ تُ", transliteration: "ta, ti, tu" },
      { arabic: "خَلَقَ", transliteration: "khalaqa" },
      { arabic: "كُتِبَ", transliteration: "kutiba" }
    ],
    practice: ["Read rows at a steady beat", "Contrast short and long sounds", "Blend three letters without pauses"],
    check: { question: "Which sound belongs to بِ?", answers: ["ba", "bi", "bu"], correct: 1 }
  },
  {
    id: "tanween", number: 5, title: "Tanween (double vowels)", arabicTitle: "التنوين",
    summary: "Read fatḥatayn, kasratayn, and ḍammatayn with a clear final n sound.",
    teacherNote: "The n sound is heard but no written nūn appears. Keep the preceding vowel short.",
    objectives: ["Recognise three tanween signs", "Add a clean n sound", "Prepare for nūn-sākinah rules"],
    examples: [
      { arabic: "بً بٍ بٌ", transliteration: "ban, bin, bun" },
      { arabic: "عَلِيمٌ", transliteration: "ʿalīmun" },
      { arabic: "غَفُورًا", transliteration: "ghafūran" },
      { arabic: "يَوْمَئِذٍ", transliteration: "yawmaʾidhin" }
    ],
    practice: ["Read all three endings", "Stop and continue to hear the difference", "Find tanween in short surahs"],
    check: { question: "What sound ends كِتَابٌ when continuing?", answers: ["-bu", "-bun", "-bū"], correct: 1 }
  },
  {
    id: "madd", number: 6, title: "Natural elongation (Madd)", arabicTitle: "المد الطبيعي",
    summary: "Stretch alif, wāw, and yā madd letters for two counts when their matching vowel comes before them.",
    teacherNote: "Use two even beats: fatḥah + alif, ḍammah + wāw, kasrah + yā.",
    objectives: ["Recognise three madd letters", "Hold two counts", "Avoid extra vowel movement"],
    examples: [
      { arabic: "بَا بُو بِي", transliteration: "bā, bū, bī" },
      { arabic: "قَالَ", transliteration: "qāla" },
      { arabic: "يَقُولُ", transliteration: "yaqūlu" },
      { arabic: "قِيلَ", transliteration: "qīla" }
    ],
    practice: ["Count two finger taps", "Contrast بَ and بَا", "Keep the long sound level"],
    check: { question: "Which pair forms a natural madd?", answers: ["بَ + و", "بُ + و", "بِ + ا"], correct: 1 }
  },
  {
    id: "standing-vowels", number: 7, title: "Standing vowels", arabicTitle: "الحركات القائمة",
    summary: "Read the dagger alif, standing kasrah, and inverted ḍammah as two-count long vowels.",
    teacherNote: "These small signs are easy to miss; scan above and below each letter before reading.",
    objectives: ["Recognise miniature long-vowel signs", "Read two counts", "Find them in familiar phrases"],
    examples: [
      { arabic: "هَٰذَا", transliteration: "hādhā" },
      { arabic: "ٱلرَّحْمَٰنِ", transliteration: "ar-Raḥmāni" },
      { arabic: "بِهِۦ", transliteration: "bihī" },
      { arabic: "إِنَّهُۥ", transliteration: "innahū" }
    ],
    practice: ["Highlight every small standing sign", "Read with two taps", "Compare printed mushaf styles"],
    check: { question: "The small alif in هَٰذَا is held for…", answers: ["one count", "two counts", "six counts always"], correct: 1 }
  },
  {
    id: "leen", number: 8, title: "Soft letters (Leen)", arabicTitle: "حروف اللين",
    summary: "Read wāw or yā sākinah after fatḥah with a smooth, soft glide.",
    teacherNote: "Leen is not the same as natural madd. When stopping, it may be lengthened according to madd ʿāriḍ rules.",
    objectives: ["Recognise fatḥah before silent wāw/yā", "Produce a soft glide", "Practise stopping"],
    examples: [
      { arabic: "خَوْف", transliteration: "khawf" },
      { arabic: "بَيْت", transliteration: "bayt" },
      { arabic: "قُرَيْش", transliteration: "Quraysh" },
      { arabic: "ٱلصَّيْف", transliteration: "aṣ-ṣayf" }
    ],
    practice: ["Contrast بُو with بَوْ", "Read while continuing", "Read while stopping"],
    check: { question: "Which word contains yā leen?", answers: ["قِيل", "بَيْت", "يَقُول"], correct: 1 }
  },
  {
    id: "sukoon", number: 9, title: "Sukoon and joining", arabicTitle: "السكون",
    summary: "Join a vowelled letter directly into a consonant carrying sukoon, without inserting an extra vowel.",
    teacherNote: "A sākin letter depends on the letter before it. Do not bounce unless the letter is a qalqalah letter.",
    objectives: ["Recognise sukoon", "Join consonants cleanly", "Avoid added vowels"],
    examples: [
      { arabic: "أَبْ", transliteration: "ab" },
      { arabic: "مِنْ", transliteration: "min" },
      { arabic: "يَكْتُبُ", transliteration: "yaktubu" },
      { arabic: "أَنْعَمْتَ", transliteration: "anʿamta" }
    ],
    practice: ["Read two-letter joins", "Cover the transliteration", "Check that no extra /a/ appears"],
    check: { question: "How should مِنْ be finished?", answers: ["mina", "min", "minū"], correct: 1 }
  },
  {
    id: "qalqalah", number: 10, title: "Qalqalah", arabicTitle: "القلقلة",
    summary: "Give a controlled echo to ق ط ب ج د when one of them carries sukoon.",
    teacherNote: "The echo reveals the consonant; it must not become a full fatḥah, kasrah, or ḍammah.",
    objectives: ["Memorise قطب جد", "Apply minor qalqalah", "Strengthen qalqalah when stopping"],
    examples: [
      { arabic: "قُطْبُ جَدٍ", transliteration: "quṭbu jaddin", note: "Mnemonic: ق ط ب ج د" },
      { arabic: "يَجْعَلُ", transliteration: "yajʿalu" },
      { arabic: "أَحَدْ", transliteration: "aḥad (when stopping)" },
      { arabic: "ٱلْفَلَقْ", transliteration: "al-falaq (when stopping)" }
    ],
    practice: ["Say the mnemonic", "Practise middle-of-word echoes", "Compare continuing and stopping"],
    check: { question: "Which set contains all qalqalah letters?", answers: ["ف ك ل م ن", "ق ط ب ج د", "ء ه ع ح غ خ"], correct: 1 }
  },
  {
    id: "shaddah", number: 11, title: "Shaddah (doubling)", arabicTitle: "الشدة",
    summary: "Read a mushaddad letter as two letters: the first silent and the second vowelled.",
    teacherNote: "Hold the articulation point briefly; never skip the hidden first consonant.",
    objectives: ["Recognise shaddah", "Split it mentally into two letters", "Join before and after smoothly"],
    examples: [
      { arabic: "إِنَّ", transliteration: "inna" },
      { arabic: "رَبِّ", transliteration: "rabbi" },
      { arabic: "ثُمَّ", transliteration: "thumma" },
      { arabic: "ٱلْحَقُّ", transliteration: "al-ḥaqqu" }
    ],
    practice: ["Tap twice on the doubled consonant", "Contrast إِنَ and إِنَّ", "Read shaddah with each vowel"],
    check: { question: "A letter with shaddah represents…", answers: ["one silent letter", "two joined instances of the letter", "a long vowel"], correct: 1 }
  },
  {
    id: "ghunnah", number: 12, title: "Nūn and mīm with shaddah", arabicTitle: "غنة النون والميم المشددتين",
    summary: "Hold a nasal ghunnah for two counts on نّ and مّ.",
    teacherNote: "The sound resonates through the nasal passage while the tongue or lips keep the correct articulation.",
    objectives: ["Identify mandatory ghunnah", "Hold two counts", "Keep nūn and mīm distinct"],
    examples: [
      { arabic: "إِنَّ", transliteration: "inna" },
      { arabic: "ثُمَّ", transliteration: "thumma" },
      { arabic: "مِنَ ٱلْجِنَّةِ وَٱلنَّاسِ", transliteration: "mina al-jinnati wan-nās" }
    ],
    practice: ["Hold the nose lightly to sense resonance", "Count two beats", "Do not extend the following vowel"],
    check: { question: "How long is the ghunnah on نّ and مّ?", answers: ["one count", "two counts", "always six counts"], correct: 1 }
  },
  {
    id: "noon-rules", number: 13, title: "Nūn sākinah and tanween", arabicTitle: "أحكام النون الساكنة والتنوين",
    summary: "Apply iẓhār, idghām, iqlāb, or ikhfā according to the following letter.",
    teacherNote: "First find silent nūn or tanween, then look exactly one letter ahead before choosing the rule.",
    objectives: ["Know the four rules", "Memorise rule-letter groups", "Apply correct ghunnah"],
    examples: [
      { arabic: "مِنْ هَادٍ", transliteration: "min hādin", note: "Iẓhār: clear before throat letters ء ه ع ح غ خ." },
      { arabic: "مَن يَقُولُ", transliteration: "may-yaqūlu", note: "Idghām with ghunnah: ي ن م و." },
      { arabic: "مِن رَّبِّهِمْ", transliteration: "mir-rabbihim", note: "Idghām without ghunnah: ل ر." },
      { arabic: "سَمِيعٌ بَصِيرٌ", transliteration: "samīʿum-baṣīr", note: "Iqlāb before ب; n becomes a concealed m." },
      { arabic: "مِنْ شَرٍّ", transliteration: "min-sharrin", note: "Ikhfā before the remaining 15 letters." }
    ],
    practice: ["Sort examples into four rules", "Underline the trigger letter", "Recite with a teacher"],
    check: { question: "Which rule applies before ب?", answers: ["Iẓhār", "Iqlāb", "Idghām without ghunnah"], correct: 1 }
  },
  {
    id: "meem-rules", number: 14, title: "Mīm sākinah", arabicTitle: "أحكام الميم الساكنة",
    summary: "Apply ikhfā shafawī before ب, idghām shafawī before م, and clear iẓhār shafawī before all other letters.",
    teacherNote: "Keep both lips gently together. Do not exaggerate the nasal sound in iẓhār.",
    objectives: ["Know three mīm rules", "Recognise labial triggers", "Control lip closure"],
    examples: [
      { arabic: "تَرْمِيهِمْ بِحِجَارَةٍ", transliteration: "tarmīhim-biḥijāratin", note: "Ikhfā shafawī before bā." },
      { arabic: "لَهُم مَّا", transliteration: "lahum-mā", note: "Idghām shafawī before mīm." },
      { arabic: "هُمْ فِيهَا", transliteration: "hum fīhā", note: "Iẓhār shafawī before other letters." }
    ],
    practice: ["Watch lip position in a mirror", "Mark the next letter", "Practise without breaking airflow"],
    check: { question: "Mīm sākinah before another mīm uses…", answers: ["iqlāb", "idghām shafawī", "qalqalah"], correct: 1 }
  },
  {
    id: "lam-ra", number: 15, title: "Lām, Rā and solar letters", arabicTitle: "أحكام اللام والراء",
    summary: "Differentiate moon and sun lām, and learn the basic heavy/light rules for Allah's lām and the letter rā.",
    teacherNote: "The lām of Allah is heavy after fatḥah or ḍammah and light after kasrah. Rā has additional exceptions to study with a teacher.",
    objectives: ["Recognise sun and moon letters", "Apply lām of Allah", "Begin tafkhīm and tarqīq of rā"],
    examples: [
      { arabic: "ٱلْقَمَرُ", transliteration: "al-qamar", note: "Moon letter: lām is pronounced." },
      { arabic: "ٱلشَّمْسُ", transliteration: "ash-shams", note: "Sun letter: lām assimilates into shīn." },
      { arabic: "قَالَ ٱللَّهُ", transliteration: "qāla Allāhu", note: "Heavy lām." },
      { arabic: "بِسْمِ ٱللَّهِ", transliteration: "bismi Allāhi", note: "Light lām after kasrah." },
      { arabic: "رِزْقٌ / رَبٌّ", transliteration: "rizqun / rabbun", note: "A basic light/heavy rā contrast." }
    ],
    practice: ["Sort sun and moon letters", "Contrast Allah after different vowels", "Have a teacher check rā exceptions"],
    check: { question: "In ٱلشَّمْسُ the lām is…", answers: ["clearly pronounced", "assimilated into shīn", "changed into mīm"], correct: 1 }
  },
  {
    id: "advanced-madd", number: 16, title: "Extended madd rules", arabicTitle: "أقسام المد",
    summary: "Distinguish connected, separated, compulsory, and temporary elongation from natural two-count madd.",
    teacherNote: "Counting conventions may vary by recognised recitation method. This course follows Ḥafṣ ʿan ʿĀṣim; stay consistent with your teacher.",
    objectives: ["Recognise hamzah or sukoon causes", "Differentiate common madd types", "Keep counts consistent"],
    examples: [
      { arabic: "جَاءَ", transliteration: "jāʾa", note: "Madd muttaṣil: hamzah in the same word." },
      { arabic: "فِيٓ أَنفُسِكُمْ", transliteration: "fī anfusikum", note: "Madd munfaṣil: hamzah starts the next word." },
      { arabic: "ٱلضَّآلِّينَ", transliteration: "aḍ-ḍāllīn", note: "Madd lāzim: compulsory six counts." },
      { arabic: "ٱلْعَٰلَمِينَ", transliteration: "al-ʿālamīn", note: "When stopping: madd ʿāriḍ, 2, 4, or 6 counts." }
    ],
    practice: ["Label the cause after each madd", "Use finger counts", "Recite one passage with consistent timing"],
    check: { question: "Which word demonstrates madd lāzim?", answers: ["قَالَ", "ٱلضَّآلِّينَ", "بِهِ"], correct: 1 }
  },
  {
    id: "waqf", number: 17, title: "Stopping signs and fluent recitation", arabicTitle: "علامات الوقف",
    summary: "Use mushaf stopping signs, start again from a sound meaning, and combine all Qaida rules in complete passages.",
    teacherNote: "Never choose a stop that damages the meaning. When breath forces a poor stop, restart from an appropriate earlier phrase.",
    objectives: ["Recognise common waqf signs", "Apply end-of-word changes", "Recite short surahs fluently"],
    examples: [
      { arabic: "مـ", transliteration: "Stop", note: "Necessary stop (waqf lāzim)." },
      { arabic: "لا", transliteration: "Do not stop", note: "Stopping here may harm the connection of meaning." },
      { arabic: "ج", transliteration: "Stopping or continuing permitted" },
      { arabic: "قلى / صلى", transliteration: "Stopping preferred / continuing preferred" },
      { arabic: "ۛ  ۛ", transliteration: "Paired stop", note: "Stop at one of the two marks, not both." }
    ],
    practice: ["Mark stops before reading", "Practise the last ten surahs", "Complete a teacher-observed final reading"],
    check: { question: "What does لا usually tell the reader?", answers: ["A necessary stop", "Do not stop here", "Repeat the verse"], correct: 1 }
  }
];

export const QAIDA_TOTAL = QAIDA_LESSONS.length;
